//#region src/utils/csv-parser.ts
/**
* Parses a CSV string into structured data with columns and rows.
*
* @param csv - The CSV string to parse
* @returns Promise resolving to an object containing columns (header names) and rows (data arrays)
*
* @throws {Error} When CSV input is invalid, empty, or malformed
* @throws {Error} When header row is missing or contains duplicates
* @throws {Error} When data rows have mismatched column counts
* @throws {Error} When CSV contains unclosed quotes or other parsing errors
*
* @example
* ```typescript
* const csvString = `name,age,city
* "John Doe",30,"New York"
* "Jane Smith",25,Boston`;
*
* try {
*   const result = await parseCsv(csvString);
*   console.log(result.columns); // ['name', 'age', 'city']
*   console.log(result.rows);    // [['John Doe', '30', 'New York'], ['Jane Smith', '25', 'Boston']]
* } catch (error) {
*   console.error('CSV parsing failed:', error.message);
* }
* ```
*
* @example
* ```typescript
* // Handles quoted fields with commas and escaped quotes
* const csvWithQuotes = `product,description,price
* "Widget A","A useful widget, very popular","$19.99"
* "Widget ""Pro""","An advanced widget with ""smart"" features","$29.99"`;
*
* const result = await parseCsv(csvWithQuotes);
* // Properly parses descriptions with commas and escaped quotes
* ```
*/
async function parseCsv(csv) {
	if (!csv || typeof csv !== "string") throw new Error("CSV input must be a non-empty string");
	csv = csv.trim();
	if (!csv) throw new Error("CSV file appears to be empty");
	const columns = [];
	const rows = [];
	let currentRow = [];
	let current = "";
	let inQuotes = false;
	let i = 0;
	const len = csv.length;
	let dataRowCount = 0;
	while (i < len) {
		const char = csv[i];
		const nextChar = i + 1 < len ? csv[i + 1] : void 0;
		if (char === "\"") {
			if (inQuotes && nextChar === "\"") {
				current += "\"";
				i += 2;
				continue;
			} else {
				inQuotes = !inQuotes;
				i += 1;
				continue;
			}
		} else if (char === "," && !inQuotes) {
			currentRow.push(current.trim());
			current = "";
			i += 1;
			continue;
		} else if ((char === "\n" || char === "\r") && !inQuotes) {
			if (current || currentRow.length > 0) {
				currentRow.push(current.trim());
				if (columns.length === 0) {
					columns.push(...currentRow);
					const duplicateColumns = findDuplicates(columns);
					if (duplicateColumns.length > 0) throw new Error(`Duplicate column names found: ${duplicateColumns.join(", ")}`);
				} else {
					dataRowCount++;
					if (currentRow.length !== columns.length) throw new Error(`Row ${dataRowCount + 1} has ${currentRow.length} columns but header has ${columns.length} columns`);
					rows.push(currentRow);
				}
				currentRow = [];
				current = "";
			}
			if (char === "\r" && nextChar === "\n") i += 2;
			else i += 1;
			continue;
		} else {
			current += char;
			i += 1;
		}
	}
	if (current || currentRow.length > 0) {
		currentRow.push(current.trim());
		if (columns.length === 0) {
			columns.push(...currentRow);
			const duplicateColumns = findDuplicates(columns);
			if (duplicateColumns.length > 0) throw new Error(`Duplicate column names found: ${duplicateColumns.join(", ")}`);
		} else {
			dataRowCount++;
			if (currentRow.length !== columns.length) throw new Error(`Row ${dataRowCount + 1} has ${currentRow.length} columns but header has ${columns.length} columns`);
			rows.push(currentRow);
		}
	}
	if (inQuotes) throw new Error("Unclosed quote found in CSV");
	if (columns.length === 0) throw new Error("Header row cannot be empty");
	if (rows.length === 0) throw new Error("CSV file contains no valid data rows");
	return {
		columns,
		rows
	};
}
function findDuplicates(arr) {
	const seen = /* @__PURE__ */ new Set();
	const duplicates = /* @__PURE__ */ new Set();
	for (const item of arr) if (seen.has(item)) duplicates.add(item);
	else seen.add(item);
	return Array.from(duplicates);
}
//#endregion
export { parseCsv };

//# sourceMappingURL=csv-parser.js.map